import { useCallback, useEffect, useState } from 'react';
import {
  getDeferredPrompt,
  isIOS,
  isStandalone,
  promptInstall,
  subscribe,
} from '../lib/pwaInstall';

// "Not now" snoozes rather than hides forever — the button only ever appears
// once the browser has said installation is actually possible, so a permanent
// opt-out from a single tap would be the wrong trade.
const DISMISS_KEY = 'ap_install_dismissed_until';
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function snoozeActive() {
  try {
    const until = parseInt(window.localStorage.getItem(DISMISS_KEY), 10);
    return Number.isFinite(until) && Date.now() < until;
  } catch (err) {
    // Safari in private mode throws on localStorage access.
    return false;
  }
}

/**
 * Drives the "Install App" affordance in the Welcome back banner.
 *
 * `canInstall` stays false until we know which path applies: a captured
 * beforeinstallprompt (Chromium desktop/Android) or iOS. Anything else —
 * Firefox, an unsupported browser, or Chrome before its engagement heuristic
 * fires — shows nothing rather than a button that would do nothing.
 */
export default function usePwaInstall() {
  const [hasPrompt, setHasPrompt] = useState(() => !!getDeferredPrompt());
  const [standalone, setStandalone] = useState(isStandalone);
  const [snoozed, setSnoozed] = useState(snoozeActive);
  const [showIosHelp, setShowIosHelp] = useState(false);

  // The event may land before OR after this mounts, so both are covered:
  // the initializer above reads what was already buffered, this picks up
  // anything that arrives later.
  useEffect(() => subscribe(() => setHasPrompt(!!getDeferredPrompt())), []);

  // Catches an install that completes while the tab is open.
  useEffect(() => {
    const mql = window.matchMedia && window.matchMedia('(display-mode: standalone)');
    if (!mql || !mql.addEventListener) return undefined;
    const onChange = () => setStandalone(isStandalone());
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const ios = isIOS();
  const canInstall = !standalone && !snoozed && (hasPrompt || ios);

  const install = useCallback(() => {
    if (getDeferredPrompt()) {
      promptInstall();
      return;
    }
    // iOS has nothing to trigger, so tell the user where the control lives.
    if (isIOS()) setShowIosHelp(true);
  }, []);

  const dismiss = useCallback(() => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now() + SNOOZE_MS));
    } catch (err) {
      /* storage unavailable — the snooze just won't survive the reload */
    }
    setSnoozed(true);
    setShowIosHelp(false);
  }, []);

  const closeIosHelp = useCallback(() => setShowIosHelp(false), []);

  return { canInstall, install, dismiss, showIosHelp, closeIosHelp };
}
